/**
 * API сервисы для работы с FastAPI backend v2.0
 * 
 * Реализует методы для всех основных сущностей:
 * - Users
 * - Objects
 * - Organizations
 * - Roles & Permissions
 */

import { get, post, put, del, patch } from "@/lib/api-client-v2";
import type { ObjectCardResponse } from "@/types/card-template";
import type {
  User,
  UserUpdate,
  RegisterRequest,
  GeoObject,
  GeoObjectCreate,
  GeoObjectUpdate,
  GeoObjectWithRelations,
  ObjectOrganizationLink,
  ObjectOrganizationLinkCreate,
  ObjectOrganizationLinkUpdate,
  Geometry,
  GeometryCreate,
  GeometryUpdate,
  GeometryAuditEntry,
  ObjectCardAuditEntry,
  ObjectCardAuditQueryParams,
  GeometryAnalyzeRequestBody,
  GeometryAnalyzeResponse,
  GeometryCompatibleObjectKlsResponse,
  File,
  FilestoreNodeBrief,
  FilestoreFolderCreate,
  FilestorePresignInitRequest,
  FilestorePresignInitResponse,
  FilestorePresignCompleteRequest,
  FilestoreMultipartInitRequest,
  FilestoreMultipartInitResponse,
  FilestoreMultipartPartUrlsRequest,
  FilestoreMultipartPartUrlsResponse,
  FilestoreMultipartCompleteRequest,
  Organization,
  OrganizationCreate,
  OrganizationUpdate,
  OrganizationRelationshipRole,
  Role,
  RoleCreate,
  RoleUpdate,
  RoleWithPermissions,
  Permission,
  ObjectType,
  ObjectTypeCreate,
  ObjectTypeUpdate,
  SystemStats,
  ObjectListSummary,
  ServiceIntegration,
  ServiceIntegrationCreate,
  ServiceIntegrationUpdate,
  CadastralDbConfig,
  CadastralDbConfigTestResponse,
  RoleQuota,
  RoleQuotaCreate,
  RoleQuotaUpdate,
  UserQuota,
  UserQuotaCreate,
  UserQuotaUpdate,
  BulkRoleQuotaCreate,
  BulkUserQuotaCreate,
  StorageStats,
  StorageSettings,
  StorageSettingsUpdate,
  TestConnectionRequest,
  TestConnectionResponse,
  CleanupResponse,
  IntegrityCheckResponse,
  OptimizeResponse,
  OverpassStats,
  OverpassSettings,
  SearchStats,
  ApiKey,
  ApiKeyWithSecret,
  ApiKeyCreate,
  ApiKeyUpdate,
  ApiUsageStats,
  ApiKeyUsageStats,
  ApiWebhook,
  ApiWebhookCreate,
  ApiWebhookUpdate,
  WebhookDeliveryRecord,
  BotSetting,
  BotSettingCreate,
  BotSettingUpdate,
  IncidentType,
  IncidentTypeCreate,
  IncidentTypeUpdate,
  TelegramUser,
  TelegramUserCreate,
  TelegramUserUpdate,
  TelegramIncident,
  TelegramIncidentCreate,
  TelegramIncidentUpdate,
  CustomTag,
  CustomTagCreate,
  CustomTagUpdate,
  ObjectTag,
  ObjectTagCreate,
  GeographyUnit,
  GeographyUnitCreate,
  GeographyUnitUpdate,
  BoundaryFeature,
  BoundaryQuery,
  DataAccessTemplate,
  DataAccessTemplateCreate,
  DataAccessTemplateUpdate,
  ObjectAccessPolicy,
  ObjectAccessPolicyCreate,
  LayerAccessPolicy,
  LayerAccessPolicyCreate,
  CadastralInfo,
  DetailedCadastralInfo,
  CadastralCacheSettings,
  CadastralCacheStatistics,
  CadastreExternalSearchResponse,
  CadastreExternalSourceModeResponse,
  CadastreExternalHierarchyResponse,
  CadastreExternalParcelSummary,
  McpConnection,
  McpConnectionCreate,
  McpConnectionUpdate,
  McpConnectionTestResult,
  McpServerStatus,
  AdminSystemSettings,
  AdminSystemSettingsUpdate,
} from "@/types/api-v2";

// ============================================================================
// Users API
// ============================================================================

export const usersApi = {
  /**
   * Получить список пользователей (требует права администратора)
   */
  list: (params?: { skip?: number; limit?: number }) =>
    get<User[]>("/users", params),

  /**
   * Получить пользователя по ID
   */
  get: (userId: string) =>
    get<User>(`/users/${userId}`),

  /**
   * Обновить пользователя
   */
  update: (userId: string, data: UserUpdate) =>
    put<User>(`/users/${userId}`, data),

  /**
   * Удалить пользователя
   */
  delete: (userId: string) =>
    del(`/users/${userId}`),

  /**
   * Создать пользователя (админ; не подменяет JWT текущего сеанса)
   */
  create: (data: RegisterRequest) =>
    post<User>("/users", data),
};

// ============================================================================
// Objects API
// ============================================================================

export const objectsApi = {
  /**
   * Получить список объектов
   */
  list: (params?: {
    skip?: number;
    limit?: number;
    organization_id?: number;
    type_id?: number;
    status?: string;
    /** Поиск по name/full_name; числовой id при чисто цифровой строке */
    q?: string;
    geography_ids?: string;
    geography_combine?: "any" | "all";
  }) =>
    get<GeoObject[]>("/objects", params),

  /**
   * COUNT объектов и геометрий по фильтрам списка (без пагинации).
   */
  summary: (params?: {
    organization_id?: number;
    kls_id?: number;
    status?: string;
    organization_ids?: string;
    kls_ids?: string;
    statuses?: string;
    q?: string;
    geography_ids?: string;
    geography_combine?: "any" | "all";
  }) => get<ObjectListSummary>("/objects/summary", params),

  /**
   * Создать объект
   */
  create: (data: GeoObjectCreate) =>
    post<GeoObject>("/objects", data),

  /**
   * Получить объект по ID
   */
  get: (objectId: number) =>
    get<GeoObjectWithRelations>(`/objects/${objectId}`),

  /**
   * Обновить объект
   */
  update: (objectId: number, data: GeoObjectUpdate) =>
    put<GeoObject>(`/objects/${objectId}`, data),

  /**
   * Удалить объект
   */
  delete: (objectId: number) =>
    del(`/objects/${objectId}`),

  /**
   * Получить геометрии объекта
   */
  getGeometries: (objectId: number) =>
    get<Geometry[]>(`/objects/${objectId}/geometries`),

  /**
   * Добавить геометрию к объекту
   */
  addGeometry: (objectId: number, data: GeometryCreate) =>
    post<Geometry>(`/objects/${objectId}/geometries`, data),

  /**
   * Обновить геометрию объекта
   */
  updateGeometry: (objectId: number, geometryId: number, data: GeometryUpdate) =>
    patch<Geometry>(`/objects/${objectId}/geometries/${geometryId}`, data),

  /**
   * Удалить геометрию объекта
   */
  deleteGeometry: (objectId: number, geometryId: number) =>
    del(`/objects/${objectId}/geometries/${geometryId}`),

  /**
   * Журнал изменений геометрии (плоский маршрут /geometries/{id}/audit)
   */
  getGeometryAudit: (geometryId: number, limit = 50) =>
    get<GeometryAuditEntry[]>(`/geometries/${geometryId}/audit`, { limit }),

  /**
   * Журнал изменений атрибутов карточки на уровне объекта (PUT …/card, поля object).
   * Поддерживает фильтры по времени, пользователю, полю и подстроке поиска.
   */
  getObjectCardAudit: (objectId: number, params?: ObjectCardAuditQueryParams) => {
    const p: Record<string, string | number> = {
      limit: params?.limit ?? 200,
    };
    if (params?.action) p.action = params.action;
    if (params?.user_id) p.user_id = params.user_id;
    if (params?.field_key) p.field_key = params.field_key;
    if (params?.search) p.search = params.search;
    if (params?.created_from) p.created_from = params.created_from;
    if (params?.created_to) p.created_to = params.created_to;
    return get<ObjectCardAuditEntry[]>(`/objects/${objectId}/card/audit`, p);
  },

  /**
   * Восстановить атрибуты объекта из снимка в журнале (требует objects.card_audit.restore).
   */
  restoreObjectCardFromAudit: (objectId: number, auditLogId: number) =>
    post<ObjectCardResponse>(
      `/objects/${objectId}/card/audit/${auditLogId}/restore`,
      {},
    ),

  /**
   * Получить файлы объекта
   */
  getFiles: (objectId: number) =>
    get<File[]>(`/objects/${objectId}/files`),

  /**
   * Дерево filestore: дочерние узлы папки (`parentId` null — корень контейнера).
   */
  getFilestoreNodes: (objectId: number, parentId: number | null) =>
    get<FilestoreNodeBrief[]>(
      `/objects/${objectId}/filestore/nodes`,
      parentId != null ? { parent_id: parentId } : undefined,
    ),

  /**
   * Создать папку в контейнере объекта.
   */
  createFilestoreFolder: (objectId: number, data: FilestoreFolderCreate) =>
    post<{ id: number; name: string; parent_id: number | null; kind: string }>(
      `/objects/${objectId}/filestore/folders`,
      { ...data, parent_id: data.parent_id ?? undefined },
    ),

  /**
   * Presigned-загрузка: init (квоты, pending, URL для PUT), затем fetch PUT на upload_url, затем complete.
   */
  filestorePresignInit: (objectId: number, body: FilestorePresignInitRequest) =>
    post<FilestorePresignInitResponse>(`/objects/${objectId}/files/presign-init`, body),

  filestorePresignComplete: (objectId: number, body: FilestorePresignCompleteRequest) =>
    post<File>(`/objects/${objectId}/files/presign-complete`, body),

  filestoreMultipartInit: (objectId: number, body: FilestoreMultipartInitRequest) =>
    post<FilestoreMultipartInitResponse>(`/objects/${objectId}/files/multipart/init`, body),

  filestoreMultipartPartUrls: (
    objectId: number,
    blobId: number,
    body: FilestoreMultipartPartUrlsRequest,
  ) =>
    post<FilestoreMultipartPartUrlsResponse>(
      `/objects/${objectId}/files/multipart/${blobId}/part-urls`,
      body,
    ),

  filestoreMultipartComplete: (
    objectId: number,
    blobId: number,
    body: FilestoreMultipartCompleteRequest,
  ) => post<File>(`/objects/${objectId}/files/multipart/${blobId}/complete`, body),

  /** Связи объекта с организациями (KLS) */
  getOrganizationLinks: (objectId: number) =>
    get<ObjectOrganizationLink[]>(`/objects/${objectId}/organization-links`),

  createOrganizationLink: (
    objectId: number,
    data: ObjectOrganizationLinkCreate,
  ) =>
    post<ObjectOrganizationLink>(
      `/objects/${objectId}/organization-links`,
      data,
    ),

  updateOrganizationLink: (
    objectId: number,
    linkId: number,
    data: ObjectOrganizationLinkUpdate,
  ) =>
    put<ObjectOrganizationLink>(
      `/objects/${objectId}/organization-links/${linkId}`,
      data,
    ),

  deleteOrganizationLink: (objectId: number, linkId: number) =>
    del(`/objects/${objectId}/organization-links/${linkId}`),
};

// ============================================================================
// Geometries API (плоские маршруты /geometries/...)
// ============================================================================

export const geometriesApi = {
  /** Контур геометрии по id (фокус «На карте» в workspace модулей). */
  getById: (geometryId: number) => get<Geometry>(`/geometries/${geometryId}`),

  /**
   * Пространственный анализ нарисованной геометрии (пересечения с объектами в доступной области).
   */
  analyze: (body: GeometryAnalyzeRequestBody) =>
    post<GeometryAnalyzeResponse>("/geometries/analyze", body),

  compatibleObjectTypes: (params: {
    organization_id: number;
    geometry_semantic_kls_id: number;
  }) =>
    get<GeometryCompatibleObjectKlsResponse>("/geometries/compatible-object-types", {
      organization_id: params.organization_id,
      geometry_semantic_kls_id: params.geometry_semantic_kls_id,
    }),

  osmReview: (geometryId: number, body: import("@/types/osm-import").OsmGeometryReviewRequest) =>
    post<import("@/types/osm-import").OsmGeometryReviewResponse>(
      `/geometries/${geometryId}/osm-review`,
      body,
    ),
};

export const osmImportsApi = {
  create: (body: import("@/types/osm-import").OsmImportCreateRequest) =>
    post<import("@/types/osm-import").OsmImportCreateResponse>("/osm-imports/", body),

  reviewQueue: (batchId: string, params?: { offset?: number; limit?: number }) =>
    get<import("@/types/osm-import").OsmReviewQueueResponse>(
      `/osm-imports/${batchId}/review`,
      params,
    ),

  bulkReview: (batchId: string, action: "approve_all" | "reject_all") =>
    post<{ updated: number }>(
      `/osm-imports/${batchId}/review/bulk?action=${encodeURIComponent(action)}`,
    ),

  publish: (batchId: string) =>
    post<{ published: number; removed: number }>(`/osm-imports/${batchId}/publish`),

  cancel: (batchId: string) =>
    del<{ status: string; removed_objects: number; removed_geometries: number }>(
      `/osm-imports/${batchId}`,
    ),
};

// ============================================================================
// Organizations API
// ============================================================================

export const organizationsApi = {
  /**
   * Получить список организаций
   */
  list: (params?: { skip?: number; limit?: number }) =>
    get<Organization[]>("/organizations", params),

  /**
   * Справочник ролей связи объект–организация (KLS ORGANIZATION_ROLES).
   */
  listRelationshipRoles: () =>
    get<OrganizationRelationshipRole[]>("/organizations/relationship-roles"),

  /**
   * Создать организацию
   */
  create: (data: OrganizationCreate) =>
    post<Organization>("/organizations", data),

  /**
   * Получить организацию по ID
   */
  get: (orgId: number) =>
    get<Organization>(`/organizations/${orgId}`),

  /**
   * Обновить организацию
   */
  update: (orgId: number, data: OrganizationUpdate) =>
    put<Organization>(`/organizations/${orgId}`, data),

  /**
   * Удалить организацию
   * @deprecated Организации управляются через KLS API
   */
  delete: (orgId: number) =>
    del(`/organizations/${orgId}`),

  // types API удален - типы организаций теперь не используются
  // Организации хранятся в KLS (qual_id = 336), иерархия через rubrika
};

// ============================================================================
// Roles API
// ============================================================================

export const rolesApi = {
  /**
   * Получить список ролей (требует права администратора)
   */
  list: (params?: { skip?: number; limit?: number }) =>
    get<Role[]>("/roles", params),

  /**
   * Создать роль
   */
  create: (data: RoleCreate) =>
    post<Role>("/roles", data),

  /**
   * Получить роль по ID с разрешениями
   */
  get: (roleId: number) =>
    get<RoleWithPermissions>(`/roles/${roleId}`),

  /**
   * Обновить роль
   */
  update: (roleId: number, data: RoleUpdate) =>
    put<Role>(`/roles/${roleId}`, data),

  /**
   * Удалить роль
   */
  delete: (roleId: number) =>
    del(`/roles/${roleId}`),
};

// ============================================================================
// Permissions API
// ============================================================================

export const permissionsApi = {
  /**
   * Получить список разрешений
   */
  list: (params?: { skip?: number; limit?: number; category?: string }) =>
    get<Permission[]>("/permissions", params),

  /**
   * Получить разрешение по ID
   */
  get: (permissionId: number) =>
    get<Permission>(`/permissions/${permissionId}`),
};

// ============================================================================
// Object Types API
// ============================================================================

export const objectTypesApi = {
  /**
   * Получить типы объектов
   */
  list: () =>
    get<ObjectType[]>("/object-types"),

  /**
   * Получить корневые типы объектов
   */
  roots: () =>
    get<ObjectType[]>("/object-types/roots"),

  /**
   * Получить тип объекта по ID
   */
  get: (typeId: number) =>
    get<ObjectType>(`/object-types/${typeId}`),

  /**
   * Создать тип объекта
   */
  create: (data: ObjectTypeCreate) =>
    post<ObjectType>("/object-types", data),

  /**
   * Обновить тип объекта
   */
  update: (typeId: number, data: ObjectTypeUpdate) =>
    put<ObjectType>(`/object-types/${typeId}`, data),

  /**
   * Удалить тип объекта
   */
  delete: (typeId: number) =>
    del(`/object-types/${typeId}`),
};

// ============================================================================
// Stats API
// ============================================================================

export const statsApi = {
  /**
   * Получить системную статистику
   */
  getSystemStats: () =>
    get<SystemStats>("/stats"),

  /**
   * Получить статистику активности пользователей
   */
  getUserActivityStats: () =>
    get<{ users: Array<{
      userId: string;
      username?: string;
      email?: string;
      firstName?: string;
      lastName?: string;
      fullName?: string;
      lastActive?: string;
      lastLogin?: string;
      registeredDate: string;
      objectsCreated: number;
      geometriesCreated: number;
      totalContributions: number;
      activityStatus: string;
      isActive: boolean;
      roleId?: number;
      isActiveThisWeek: boolean;
    }> }>("/stats/user-activity"),

  /**
   * Получить статистику активности организаций
   */
  getOrganizationActivityStats: () =>
    get<{ organizations: Array<{
      id: number;
      name: string;
      description?: string;
      typeId?: number;
      parentId?: number;
      objects: number;
      geometries: number;
      users: number;
      activeUsers: number;
      totalContributions: number;
      userActivityRate: number;
      activityLevel: string;
    }> }>("/stats/organization-activity"),
};

// ============================================================================
// Service Integrations API
// ============================================================================

export const serviceIntegrationsApi = {
  /**
   * Получить список интеграций
   */
  list: () =>
    get<ServiceIntegration[]>("/service-integrations"),

  /**
   * Получить активные интеграции
   */
  getActive: (type?: string) =>
    get<ServiceIntegration[]>("/service-integrations/active", type ? { type } : undefined),

  /**
   * Получить интеграцию по ID
   */
  get: (id: number) =>
    get<ServiceIntegration>(`/service-integrations/${id}`),

  /**
   * Создать интеграцию
   */
  create: (data: ServiceIntegrationCreate) =>
    post<ServiceIntegration>("/service-integrations", data),

  /**
   * Обновить интеграцию
   */
  update: (id: number, data: ServiceIntegrationUpdate) =>
    put<ServiceIntegration>(`/service-integrations/${id}`, data),

  /**
   * Удалить интеграцию
   */
  delete: (id: number) =>
    del(`/service-integrations/${id}`),

  getCadastralDbConfig: () =>
    get<CadastralDbConfig>("/service-integrations/cadastral/db-config"),

  updateCadastralDbConfig: (data: CadastralDbConfig) =>
    put<CadastralDbConfig>("/service-integrations/cadastral/db-config", data),

  testCadastralDbConfig: (data: CadastralDbConfig) =>
    post<CadastralDbConfigTestResponse>("/service-integrations/cadastral/db-config/test", data),
};

// ============================================================================
// API Management API
// ============================================================================

export const apiManagementApi = {
  /**
   * Получить список API ключей
   */
  listKeys: () =>
    get<ApiKey[]>("/admin/api-keys"),

  /**
   * Создать API ключ
   */
  createKey: (data: ApiKeyCreate) =>
    post<ApiKeyWithSecret>("/admin/api-keys", data),

  /**
   * Обновить API ключ
   */
  updateKey: (keyId: number, data: ApiKeyUpdate) =>
    patch<ApiKey>(`/admin/api-keys/${keyId}`, data),

  /**
   * Удалить API ключ
   */
  deleteKey: (keyId: number) =>
    del(`/admin/api-keys/${keyId}`),

  /**
   * Получить статистику использования API
   */
  getUsageStats: () =>
    get<ApiUsageStats>("/admin/api-keys/usage-stats"),

  /**
   * Получить статистику по конкретному ключу
   */
  getKeyStats: (keyId: number) =>
    get<ApiKeyUsageStats>(`/admin/api-keys/${keyId}/stats`),

  /**
   * Получить список webhooks
   */
  listWebhooks: (apiKeyId?: number) =>
    get<ApiWebhook[]>("/admin/api-webhooks", apiKeyId ? { apiKeyId } : undefined),

  /**
   * Создать webhook
   */
  createWebhook: (data: ApiWebhookCreate) =>
    post<ApiWebhook>("/admin/api-webhooks", data),

  /**
   * Обновить webhook
   */
  updateWebhook: (webhookId: number, data: ApiWebhookUpdate) =>
    patch<ApiWebhook>(`/admin/api-webhooks/${webhookId}`, data),

  /**
   * Удалить webhook
   */
  deleteWebhook: (webhookId: number) =>
    del(`/admin/api-webhooks/${webhookId}`),

  /**
   * Получить логи доставки webhook
   */
  getWebhookDeliveries: (webhookId: number) =>
    get<WebhookDeliveryRecord[]>(`/admin/api-webhooks/${webhookId}/deliveries`),

  /**
   * Тестовая отправка webhook
   */
  testWebhook: (webhookId: number, eventType?: string, payload?: Record<string, any>) =>
    post(`/admin/api-webhooks/${webhookId}/test`, { eventType, payload }),
};

// ============================================================================
// Telegram API
// ============================================================================

export const telegramApi = {
  /**
   * Bot Settings
   */
  botSettings: {
    list: () =>
      get<BotSetting[]>("/admin/telegram/bot-settings"),

    get: (settingKey: string) =>
      get<BotSetting>(`/admin/telegram/bot-settings/${settingKey}`),

    create: (data: BotSettingCreate) =>
      post<BotSetting>("/admin/telegram/bot-settings", data),

    update: (settingKey: string, data: BotSettingUpdate) =>
      put<BotSetting>(`/admin/telegram/bot-settings/${settingKey}`, data),

    delete: (settingKey: string) =>
      del(`/admin/telegram/bot-settings/${settingKey}`),
  },

  /**
   * Incident Types
   */
  incidentTypes: {
    list: () =>
      get<IncidentType[]>("/admin/telegram/incident-types"),

    get: (id: number) =>
      get<IncidentType>(`/admin/telegram/incident-types/${id}`),

    create: (data: IncidentTypeCreate) =>
      post<IncidentType>("/admin/telegram/incident-types", data),

    update: (id: number, data: IncidentTypeUpdate) =>
      put<IncidentType>(`/admin/telegram/incident-types/${id}`, data),

    delete: (id: number) =>
      del(`/admin/telegram/incident-types/${id}`),
  },

  /**
   * Telegram Users
   */
  users: {
    list: () =>
      get<TelegramUser[]>("/admin/telegram/users"),

    get: (id: number) =>
      get<TelegramUser>(`/admin/telegram/users/${id}`),

    create: (data: TelegramUserCreate) =>
      post<TelegramUser>("/admin/telegram/users", data),

    update: (id: number, data: TelegramUserUpdate) =>
      put<TelegramUser>(`/admin/telegram/users/${id}`, data),

    delete: (id: number) =>
      del(`/admin/telegram/users/${id}`),
  },

  /**
   * Telegram Incidents
   */
  incidents: {
    list: (params?: { skip?: number; limit?: number; status?: string }) =>
      get<TelegramIncident[]>("/admin/telegram/incidents", params),

    get: (id: number) =>
      get<TelegramIncident>(`/admin/telegram/incidents/${id}`),

    create: (data: TelegramIncidentCreate) =>
      post<TelegramIncident>("/admin/telegram/incidents", data),

    update: (id: number, data: TelegramIncidentUpdate) =>
      put<TelegramIncident>(`/admin/telegram/incidents/${id}`, data),

    delete: (id: number) =>
      del(`/admin/telegram/incidents/${id}`),

    /**
     * Получить инциденты для карты
     */
    getForMap: (params?: { bounds?: string; status?: string }) =>
      get<TelegramIncident[]>("/telegram/incidents/map", params),
  },

  /**
   * Custom Tags
   */
  tags: {
    list: () =>
      get<CustomTag[]>("/admin/telegram/tags"),

    get: (id: number) =>
      get<CustomTag>(`/admin/telegram/tags/${id}`),

    create: (data: CustomTagCreate) =>
      post<CustomTag>("/admin/telegram/tags", data),

    update: (id: number, data: CustomTagUpdate) =>
      put<CustomTag>(`/admin/telegram/tags/${id}`, data),

    delete: (id: number) =>
      del(`/admin/telegram/tags/${id}`),
  },

  /**
   * Object Tags
   */
  objectTags: {
    list: (objectId: number) =>
      get<ObjectTag[]>(`/admin/telegram/object-tags`, { objectId }),

    create: (data: ObjectTagCreate) =>
      post<ObjectTag>("/admin/telegram/object-tags", data),

    delete: (id: number) =>
      del(`/admin/telegram/object-tags/${id}`),
  },
};

// ============================================================================
// Geography API
// ============================================================================

export const geographyApi = {
  /**
   * Получить иерархию географических единиц
   */
  hierarchy: {
    list: (params?: { level?: string; parentId?: number; countryCode?: string; isoCode?: string }) =>
      get<GeographyUnit[]>("/admin/geography/hierarchy", params),

    create: (data: GeographyUnitCreate) =>
      post<GeographyUnit>("/admin/geography/hierarchy", data),

    update: (unitId: number, data: GeographyUnitUpdate) =>
      patch<GeographyUnit>(`/admin/geography/hierarchy/${unitId}`, data),

    delete: (unitId: number) =>
      del(`/admin/geography/hierarchy/${unitId}`),
  },

  /**
   * Получить границы
   */
  boundaries: {
    get: (query: BoundaryQuery) =>
      get<BoundaryFeature[]>("/admin/geography/boundaries", query as unknown as Record<string, string | number | boolean>),

    refreshCache: (level?: string, geographyId?: number, zoom?: number, force?: boolean) =>
      post("/admin/geography/boundaries/cache", { level, geographyId, zoom, force }),

    clearCache: () =>
      del("/admin/geography/boundaries/cache"),
  },

  /**
   * Публичный API для получения иерархии
   */
  public: {
    hierarchy: (params?: { level?: string; parentId?: number; countryCode?: string; isoCode?: string }) =>
      get<GeographyUnit[]>("/admin/geography/hierarchy", params),
  },

  catalog: {
    roots: (dataset: string) =>
      get<GeographyCatalogItem[]>("/admin/geography/catalog/roots", { dataset }),
    children: (parentId: number) =>
      get<GeographyCatalogItem[]>("/admin/geography/catalog/children", { parentId }),
    search: (q: string, dataset?: string) =>
      get<GeographyCatalogItem[]>(
        "/admin/geography/catalog/search",
        dataset ? { q, dataset } : { q },
      ),
    boundary: (unitId: number) =>
      get<GeographyBoundaryPayload>(`/admin/geography/catalog/${unitId}/boundary`),
    boundaries: (ids: string) =>
      get<GeographyBoundaryCollection>("/admin/geography/catalog/boundaries", { ids }),
    atPoint: (longitude: number, latitude: number, levels: string) =>
      get<GeographyCatalogItem[]>("/admin/geography/catalog/at-point", {
        longitude,
        latitude,
        levels,
      }),
  },
};

export interface GeographyCatalogItem {
  id: number;
  name: string;
  level: string;
  dataset: string | null;
  parentId: number | null;
  sourceRubrika: string | null;
  adminLevel: number | null;
  hasChildren: boolean;
}

export interface GeographyBoundaryCollection {
  type: "FeatureCollection";
  features: GeoJSON.Feature[];
  bbox: number[] | null;
}

export interface GeographyBoundaryPayload {
  id: number;
  name: string;
  level: string;
  dataset: string | null;
  geometry: GeoJSON.Geometry | null;
  bbox: number[] | null;
}

// ============================================================================
// Access Control API
// ============================================================================

export const accessControlApi = {
  /**
   * Data Access Templates
   */
  templates: {
    list: () =>
      get<DataAccessTemplate[]>("/admin/access/templates"),

    get: (id: number) =>
      get<DataAccessTemplate>(`/admin/access/templates/${id}`),

    create: (data: DataAccessTemplateCreate) =>
      post<DataAccessTemplate>("/admin/access/templates", data),

    update: (id: number, data: DataAccessTemplateUpdate) =>
      patch<DataAccessTemplate>(`/admin/access/templates/${id}`, data),

    delete: (id: number) =>
      del(`/admin/access/templates/${id}`),
  },

  /**
   * Object Access Policies
   */
  objectPolicies: {
    list: () =>
      get<ObjectAccessPolicy[]>("/admin/access/policies/objects"),

    get: (id: number) =>
      get<ObjectAccessPolicy>(`/admin/access/policies/objects/${id}`),

    create: (data: ObjectAccessPolicyCreate) =>
      post<ObjectAccessPolicy>("/admin/access/policies/objects", data),

    update: (id: number, data: Partial<ObjectAccessPolicyCreate>) =>
      put<ObjectAccessPolicy>(`/admin/access/policies/objects/${id}`, data),

    delete: (id: number) =>
      del(`/admin/access/policies/objects/${id}`),
  },

  /**
   * Layer Access Policies
   */
  layerPolicies: {
    list: () =>
      get<LayerAccessPolicy[]>("/admin/access/policies/layers"),

    get: (id: number) =>
      get<LayerAccessPolicy>(`/admin/access/policies/layers/${id}`),

    create: (data: LayerAccessPolicyCreate) =>
      post<LayerAccessPolicy>("/admin/access/policies/layers", data),

    update: (id: number, data: Partial<LayerAccessPolicyCreate>) =>
      put<LayerAccessPolicy>(`/admin/access/policies/layers/${id}`, data),

    delete: (id: number) =>
      del(`/admin/access/policies/layers/${id}`),
  },
};

// ============================================================================
// Cadastral API
// ============================================================================

export const cadastralApi = {
  /**
   * Получить кадастровую информацию объекта
   */
  getInfo: (objectId: number) =>
    get<CadastralInfo>(`/objects/${objectId}/cadastral`),

  /**
   * Получить детальную кадастровую информацию
   */
  getDetailedInfo: (objectId: number) =>
    get<DetailedCadastralInfo>(`/objects/${objectId}/cadastral/detailed`),

  /**
   * Получить кадастровую информацию из внешнего API
   */
  fetchInfo: (objectId: number) =>
    post<CadastralInfo>(`/objects/${objectId}/cadastral/fetch`),

  /**
   * Получить детальную кадастровую информацию из внешнего API
   */
  fetchDetailedInfo: (objectId: number) =>
    post<DetailedCadastralInfo>(`/objects/${objectId}/cadastral/detailed`),

  /**
   * Обновить кадастровую информацию
   */
  updateInfo: (objectId: number, data: Partial<CadastralInfo>) =>
    put<CadastralInfo>(`/objects/${objectId}/cadastral`, data),

  /**
   * Получить настройки кэша кадастровых данных
   */
  getCacheSettings: () =>
    get<CadastralCacheSettings>("/admin/cadastral/cache/settings"),

  /**
   * Обновить настройки кэша кадастровых данных
   */
  updateCacheSettings: (data: Partial<CadastralCacheSettings>) =>
    put<CadastralCacheSettings>("/admin/cadastral/cache/settings", data),

  /**
   * Получить статистику кэша кадастровых данных
   */
  getCacheStatistics: () =>
    get<CadastralCacheStatistics>("/admin/cadastral/cache/statistics"),

  /**
   * Обновить кэш кадастровых данных
   */
  refreshCache: (data?: { bbox?: number[]; admin_levels?: number[]; force?: boolean }) =>
    post<{ message: string; updated_units: number }>("/admin/cadastral/cache/refresh", data || {}),

  externalSearch: (cadNum: string, limit = 20) =>
    get<CadastreExternalSearchResponse>("/cadastral/external/search", {
      cad_num: cadNum,
      limit,
    }),

  externalGetParcel: (cadNum: string) =>
    get<CadastreExternalParcelSummary>(`/cadastral/external/parcels/${encodeURIComponent(cadNum)}`),

  externalGetHierarchy: (pkkId: string) =>
    get<CadastreExternalHierarchyResponse>(`/cadastral/external/hierarchy/${encodeURIComponent(pkkId)}`),

  externalByBbox: (params: {
    xmin: number;
    ymin: number;
    xmax: number;
    ymax: number;
    limit?: number;
  }) => get<CadastreExternalSearchResponse>("/cadastral/external/parcels/by-bbox", params),

  externalSourceMode: () =>
    get<CadastreExternalSourceModeResponse>("/cadastral/external/source-mode"),
};

// ============================================================================
// MCP API
// ============================================================================

export const mcpApi = {
  /**
   * Получить список MCP соединений
   */
  list: () =>
    get<McpConnection[]>("/admin/mcp/connections"),

  /**
   * Получить соединение по ID
   */
  get: (id: number) =>
    get<McpConnection>(`/admin/mcp/connections/${id}`),

  /**
   * Создать соединение
   */
  create: (data: McpConnectionCreate) =>
    post<McpConnection>("/admin/mcp/connections", data),

  /**
   * Обновить соединение
   */
  update: (id: number, data: McpConnectionUpdate) =>
    put<McpConnection>(`/admin/mcp/connections/${id}`, data),

  /**
   * Удалить соединение
   */
  delete: (id: number) =>
    del(`/admin/mcp/connections/${id}`),

  /**
   * Протестировать соединение
   */
  test: (id: number) =>
    post<McpConnectionTestResult>(`/admin/mcp/connections/${id}/test`),

  /**
   * Получить статус MCP сервера
   */
  getStatus: () =>
    get<McpServerStatus>("/admin/mcp/status"),

  /**
   * Запустить MCP сервер
   */
  start: () =>
    post<{ message: string; status: string }>("/admin/mcp/start"),

  /**
   * Остановить MCP сервер
   */
  stop: () =>
    post<{ message: string; status: string }>("/admin/mcp/stop"),
};

// ============================================================================
// Admin API
// ============================================================================

export const adminApi = {
  /**
   * Получить всех пользователей (admin only)
   */
  getAllUsers: (params?: { skip?: number; limit?: number }) =>
    get<User[]>("/admin/users", params),

  /**
   * Singleton-настройки системы (экран «Настройки системы»)
   */
  getSystemSettings: () => get<AdminSystemSettings>("/admin/system-settings"),

  updateSystemSettings: (data: AdminSystemSettingsUpdate) =>
    put<AdminSystemSettings>("/admin/system-settings", data),

  /**
   * Удалить пользователя (admin only)
   */
  deleteUser: (userId: string) =>
    del(`/admin/users/${userId}`),
};

// ============================================================================
// Quota API
// ============================================================================

export const quotaApi = {
  /**
   * Получить все квоты ролей
   */
  roleQuotas: {
    list: () => get<RoleQuota[]>("/admin/role-quotas"),
    get: (id: number) => get<RoleQuota>(`/admin/role-quotas/${id}`),
    create: (data: RoleQuotaCreate) =>
      post<RoleQuota>("/admin/role-quotas", data),
    update: (id: number, data: RoleQuotaUpdate) =>
      put<RoleQuota>(`/admin/role-quotas/${id}`, data),
    delete: (id: number) => del(`/admin/role-quotas/${id}`),
    bulkCreate: (data: BulkRoleQuotaCreate) =>
      post<RoleQuota[]>("/admin/role-quotas/bulk", data),
  },

  /**
   * Получить все квоты пользователей
   */
  userQuotas: {
    list: () => get<UserQuota[]>("/admin/user-quotas"),
    get: (id: number) => get<UserQuota>(`/admin/user-quotas/${id}`),
    create: (data: UserQuotaCreate) =>
      post<UserQuota>("/admin/user-quotas", data),
    update: (id: number, data: UserQuotaUpdate) =>
      put<UserQuota>(`/admin/user-quotas/${id}`, data),
    delete: (id: number) => del(`/admin/user-quotas/${id}`),
    bulkCreate: (data: BulkUserQuotaCreate) =>
      post<UserQuota[]>("/admin/user-quotas/bulk", data),
  },
};

// ============================================================================
// Storage API
// ============================================================================

export const storageApi = {
  /**
   * Получить статистику хранилища
   */
  getStats: () => get<StorageStats>("/admin/storage/stats"),

  /**
   * Получить настройки хранилища
   */
  getSettings: () => get<StorageSettings>("/admin/storage/settings"),

  /**
   * Обновить настройки хранилища
   */
  updateSettings: (data: StorageSettingsUpdate) =>
    put<StorageSettings>("/admin/storage/settings", data),

  /**
   * Тестировать подключение к хранилищу
   */
  testConnection: (data: TestConnectionRequest) =>
    post<TestConnectionResponse>("/admin/storage/test-connection", data),

  /**
   * Очистить временные файлы
   */
  cleanup: () => post<CleanupResponse>("/admin/storage/cleanup"),

  /**
   * Проверить целостность хранилища
   */
  checkIntegrity: () =>
    post<IntegrityCheckResponse>("/admin/storage/check-integrity"),

  /**
   * Оптимизировать хранилище
   */
  optimize: () => post<OptimizeResponse>("/admin/storage/optimize"),
};

// ============================================================================
// Overpass API
// ============================================================================

export const overpassApi = {
  /**
   * Получить статистику Overpass API
   */
  getStats: () => get<OverpassStats>("/admin/overpass/stats"),

  /**
   * Получить настройки Overpass API
   */
  getSettings: () => get<OverpassSettings>("/admin/overpass/settings"),

  /**
   * Обновить настройки Overpass API
   */
  updateSettings: (data: OverpassSettings) =>
    put<OverpassSettings>("/admin/overpass/settings", data),

  /**
   * Тестировать подключение к Overpass API
   */
  testConnection: () =>
    post<{
      status: string;
      message: string;
      responseTime?: number | null;
      endpoint?: string | null;
      totalTime?: number;
    }>("/admin/overpass/test"),

  /**
   * Очистить все логи Overpass API
   */
  cleanupLogs: () =>
    post<{ status: string; message: string; deletedCount: number }>(
      "/admin/overpass/logs/cleanup"
    ),
};

// ============================================================================
// AI runtime (доступность LLM: только схема ai.llm_providers)
// ============================================================================

export interface AiRuntimeAvailability {
  usable: boolean;
  /** ai_schema | none */
  primary_path: string;
}

export const aiRuntimeApi = {
  getAvailability: () => get<AiRuntimeAvailability>("/ai/runtime-availability"),
};

// ============================================================================
// Search API
// ============================================================================

/** Ответ POST /spatial/intersect (упрощённый FeatureCollection). */
export type SpatialIntersectFeature = {
  object_id: number;
  object_name: string;
  organization_id: number;
  geometry_id: number;
  geometry?: Record<string, unknown> | null;
  distance_m?: number | null;
};

export type SpatialIntersectResponse = {
  type: string;
  features: SpatialIntersectFeature[];
  total: number;
};

export type SpatialNearestFeature = SpatialIntersectFeature & {
  distance_m?: number | null;
  road_distance_m?: number | null;
  road_duration_s?: number | null;
};

export type SpatialNearestResponse = {
  type: string;
  features: SpatialNearestFeature[];
  total: number;
  road_ok?: boolean | null;
  road_error?: string | null;
};

export const spatialApi = {
  /**
   * Геометрии объектов ОИ, пересекающие контур (ST_Intersects).
   */
  intersect: (geojson: Record<string, unknown>, limit = 1000) =>
    post<SpatialIntersectResponse>("/spatial/intersect", { geojson, limit }),

  /**
   * Ближайшие геометрии ОИ (по прямой). При with_road — доп. road_* через OSRM Table.
   */
  nearest: (
    geojson: Record<string, unknown>,
    opts?: { max_distance_m?: number; limit?: number; with_road?: boolean },
  ) =>
    post<SpatialNearestResponse>("/spatial/nearest", {
      geojson,
      max_distance_m: opts?.max_distance_m ?? 1000,
      limit: opts?.limit ?? 20,
      with_road: opts?.with_road ?? false,
    }),
};

export type RoutingLonLat = { lon: number; lat: number };

export type RoutingRouteResponse = {
  code: string;
  distance_m: number;
  duration_s: number;
  geometry: {
    type: string;
    coordinates: number[][] | number[][][];
  } & Record<string, unknown>;
};

export type RoutingTableResponse = {
  code: string;
  durations: (number | null)[][];
  distances: (number | null)[][];
};

export type RoutingCompareResponse = {
  air_distance_m: number;
  road_distance_m: number | null;
  road_duration_s: number | null;
  road_ok: boolean;
  road_error?: string | null;
  geometry?: Record<string, unknown> | null;
};

export const routingApi = {
  route: (coordinates: RoutingLonLat[]) =>
    post<RoutingRouteResponse>("/routing/route", { coordinates }),
  table: (
    coordinates: RoutingLonLat[],
    opts?: { sources?: number[]; destinations?: number[] },
  ) =>
    post<RoutingTableResponse>("/routing/table", {
      coordinates,
      sources: opts?.sources,
      destinations: opts?.destinations,
    }),
  compare: (origin: RoutingLonLat, destination: RoutingLonLat) =>
    post<RoutingCompareResponse>("/routing/compare", { origin, destination }),
};

export const searchApi = {
  /**
   * Получить статистику сервиса поиска
   */
  getStats: () => get<SearchStats>("/admin/search/stats"),

  /**
   * Тестировать подключение к сервису поиска
   */
  testConnection: () =>
    post<{ status: string; message: string; responseTime?: number }>(
      "/admin/search/test"
    ),

  /**
   * Очистить все логи сервиса поиска
   */
  cleanupLogs: () =>
    post<{ status: string; message: string; deletedCount: number }>(
      "/admin/search/logs/cleanup"
    ),
};

